import React, { useCallback, useEffect, useState, useRef } from 'react'
import Button from '@components/Button'
import ScrollContainer from '@components/ScrollContainer'
import PendingWorkOrders from '@components/workOrderList/PendingWorkOrders'
import WorkOrders from '@components/workOrderList/WorkOrders'
import WorkOrderSearchbar from '@components/workOrderList/WorkOrderSearchbar'
import useQuery from '@hooks/useQuery'
import GET_WORK_ORDERS from '@lib/queries/getWorkOrders'
import Show from '@components/Show'
import { i18n } from '@lib/i18n'
import api from '@lib/api'
import runAsync from '@lib/utils/runAsync'
import { StyleSheet, Text, View } from 'react-native'
import { colors } from '@lib/colors'
import MainLayout from '@layouts/MainLayout'
import { useIsFocused } from '@react-navigation/native'
import { useCurrentUser } from '@context/CurrentUserContext'
import useNavigation from '@hooks/useNavigation'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'
import useSentry from '@hooks/useSentry'
import { usePendingWorkOrders } from '@context/PendingWorkOrdersContext'

const COPY = i18n.en.WORK_ORDERS.LIST

interface WorkOrdersData {
  workOrders: WorkOrder[]
}

export default function () {
  // Pending work orders from DynamoDB
  const { localPendingWorkOrders, setLocalPendingWorkOrders } = usePendingWorkOrders()
  const [pendingWorkOrders, setPendingWorkOrders] = useState<WorkOrderRequest[]>([])
  const [loadingPendingWorkOrders, setLoadingPendingWorkOrders] = useState<boolean>(false)
  const [onFocusFetchExecuted, setOnFocusFetchExecuted] = useState<boolean>(false)
  const isFocused = useIsFocused()

  const mounted = useRef<boolean>(true)
  const navigation = useNavigation()
  const { currentUser, userAuthenticated, userHasCompany } = useCurrentUser()
  const { captureException } = useSentry()
  const { setSnackbarMessage } = useSnackbar()

  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const toggleSearch = () => setSearchOpen(!searchOpen)

  const [refreshing, setRefreshing] = useState<boolean>(false)
  const [searchString, setSearchString] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<WorkOrderStatusName | undefined>(undefined)

  const { data, error, loading, refetch } = useQuery<WorkOrdersData>(
    GET_WORK_ORDERS,
    {},
    {
      preventAutofetch: !userHasCompany,
      needsAuthentication: true,
    },
  )

  const loadPendingWorkOrders = async () => {
    setLoadingPendingWorkOrders(true)

    /** If logged out, get from AsyncStorage */
    if (!userAuthenticated && mounted.current) {
      setPendingWorkOrders(localPendingWorkOrders)
      setLoadingPendingWorkOrders(false)
      return
    }

    /**
     * Fetch data from DynamoDB if user is logged in
     */
    try {
      const response = await api.getPendingWorkOrders(currentUser?.email as string)
      if (mounted && response.data) {
        /** Yeah I can't avoid this and the following nested ifs I think... */

        setPendingWorkOrders(response.data)
        // Also refresh the work orders in AsyncStorage
        setLocalPendingWorkOrders(response.data)
      }
    } catch (error) {
      captureException(error)
      setSnackbarMessage('Failed to fetch pending requests', SnackbarThemes.error)
    } finally {
      if (mounted) {
        setLoadingPendingWorkOrders(false)
      }
    }
  }

  const refetchAll = async () => {
    // Await both calls in parallel
    await Promise.all([loadPendingWorkOrders(), refetch()])
  }

  /**
   * Refresh callback for scroll refresh control
   */
  const onRefresh = useCallback(async () => {
    if (!userAuthenticated) return

    setRefreshing(true)
    await refetchAll()
    setRefreshing(false)
  }, [userAuthenticated])

  /**
   * Logic to fetch on focus
   */
  useEffect(() => {
    if (isFocused && !onFocusFetchExecuted) {
      setOnFocusFetchExecuted(true)
      refetchAll()
      return
    }

    if (!isFocused && onFocusFetchExecuted) setOnFocusFetchExecuted(false)
  }, [isFocused, onFocusFetchExecuted])

  /**
   * Fetch on userAuthenticated change
   */
  useEffect(() => {
    refetchAll()
  }, [userAuthenticated])

  return (
    <MainLayout hideBackButton>
      <ScrollContainer
        refreshing={refreshing}
        onRefresh={onRefresh}
      >
        <View style={ styles.container }>
          <Text style={ styles.header }>{ COPY.HEADER }</Text>
          <Show when={false}>
            <WorkOrderSearchbar
              searchOpen={ searchOpen }
              toggleSearch={ toggleSearch }
              searchString={ searchString }
              setSearchString={ setSearchString }
            />
          </Show>

          <PendingWorkOrders
            workOrders={pendingWorkOrders}
            loading={loadingPendingWorkOrders}
          />

          <WorkOrders
            {...{
              workOrders: data?.workOrders,
              loading,
              error,
              statusFilter,
              setStatusFilter,
            }}
          />
        </View>
      </ScrollContainer>
      <View style={styles.createWorkOrderButtonBackground}>
        <Button
          onPress={() => navigation.navigate('newWorkOrder')}
          label={COPY.CREATE_WORK_ORDER}
          buttonStyles={styles.createWorkOrderButton}
        />
      </View>
    </MainLayout>
  )
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 50,
  },
  header: {
    fontSize: 24,
    fontWeight: '700',
    color: '#333',
    textAlign: 'left',
  },
  workOrderSection: {
    position: 'relative',
    marginTop: 25,
  },
  workOrderSectionHeaderWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  workOrderSectionHeader: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 10,
  },
  createWorkOrderButtonBackground: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 10,
    backgroundColor: colors.gray[50],
    shadowColor: colors.gray[50],
    shadowOffset: {
      width: 0,
      height: -10,
    },
    shadowOpacity: 1,
    shadowRadius: 10,
  },
  createWorkOrderButton: {
    width: '100%',
    backgroundColor: colors.blue[300],
  },
  noWorkOrders: {
    fontStyle: 'italic',
    color: colors.gray[500],
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 10,
  },
})
